import { useCallback, useEffect, useRef, useState } from 'react';
import type { Class } from '../../data/types';
import { getPublishTheme } from '../themes';
import type { PublishConfig } from '../types';
import { resolveWidgetOrder } from '../widgetOrder';

/**
 * Sticker Cam template render.
 *
 * Pure presentational component — given a `PublishConfig` plus the
 * project's classes (and optionally a live prediction stream), it
 * renders the published page exactly as the kid's viewers will see
 * it. The composer drops this into its preview pane; the bundle
 * generator (M2) will SSR it into a single-file HTML output.
 *
 * For the M1 preview we render a static "demo" frame: theme +
 * title + a faux camera area + a row of class chips with the kid's
 * emoji. Once the bundle pipeline lands the same component renders
 * with a live MediaStream + model output.
 */

export interface StickerCamProps {
  config: PublishConfig;
  classes: ReadonlyArray<Pick<Class, 'id' | 'name'>>;
  /** When provided, fires confetti for the matched class id. */
  liveLabelId?: string | null;
  /** Optional autoplay flag; defaults true in preview. */
  autoplay?: boolean;
}

/** Demo confidence values for the preview pane. Slowly rotates so
 *  the kid can SEE what confidence bars look like before publishing,
 *  rather than seeing flat zeros. */
function useDemoProbs(classCount: number): number[] {
  const [probs, setProbs] = useState<number[]>(() =>
    Array(classCount).fill(1 / Math.max(1, classCount)),
  );
  useEffect(() => {
    if (classCount === 0) return;
    const id = window.setInterval(() => {
      const next = Array.from({ length: classCount }, () => Math.random());
      const sum = next.reduce((a, b) => a + b, 0);
      setProbs(next.map((v) => v / sum));
    }, 1500);
    return () => window.clearInterval(id);
  }, [classCount]);
  return probs;
}

interface Burst {
  id: number;
  emoji: string;
  x: number;
  y: number;
}

export function StickerCam({ config, classes, liveLabelId = null }: StickerCamProps): JSX.Element {
  const theme = getPublishTheme(config.themeId);
  const isSidePanel = config.layoutVariant === 1;
  const [bursts, setBursts] = useState<Burst[]>([]);
  const burstId = useRef(0);

  // When a new live prediction comes in, throw a sticker burst on
  // top of the camera. Removed after the animation finishes so the
  // scene stays calm.
  const fireBurst = useCallback((emoji: string) => {
    const id = ++burstId.current;
    setBursts((bs) => [
      ...bs,
      {
        id,
        emoji,
        x: 20 + Math.random() * 60, // 20%..80% of width
        y: 20 + Math.random() * 60,
      },
    ]);
    window.setTimeout(() => {
      setBursts((bs) => bs.filter((b) => b.id !== id));
    }, 1200);
  }, []);

  useEffect(() => {
    if (!liveLabelId) return;
    const emoji = config.perClass[liveLabelId]?.emoji;
    if (emoji) fireBurst(emoji);
  }, [liveLabelId, config.perClass, fireBurst]);

  // Preview convenience: when no live model is running, demo bursts
  // every ~2s so the kid sees the page come alive.
  useEffect(() => {
    if (liveLabelId !== undefined && liveLabelId !== null) return;
    const ids = classes.map((c) => c.id);
    if (ids.length === 0) return;
    const t = window.setInterval(() => {
      const pickId = ids[Math.floor(Math.random() * ids.length)]!;
      const emoji = config.perClass[pickId]?.emoji;
      if (emoji) fireBurst(emoji);
    }, 1800);
    return () => window.clearInterval(t);
  }, [classes, config.perClass, liveLabelId, fireBurst]);

  const themeStyle: React.CSSProperties = {
    '--bl-pub-bg': theme.bg,
    '--bl-pub-fg': theme.fg,
    '--bl-pub-accent': theme.accent,
    '--bl-pub-secondary': theme.secondary,
    '--bl-pub-surface': theme.surface,
    background: theme.gradient ?? theme.bg,
    color: theme.fg,
  } as React.CSSProperties;

  // Demo state for the live widgets — rotating top class drives the
  // big banner, score, and history strip so the kid sees them tick
  // in the preview.
  const demoTop = useDemoTopLabel(classes, liveLabelId ?? null);

  // Honour the kid's saved widget order — falls back to defaults +
  // appends any newly-introduced widgets.
  const resolvedOrder = resolveWidgetOrder(config.widgetOrder);

  return (
    <div
      className={`bl-pub-stickercam${isSidePanel ? ' bl-pub-stickercam--side' : ''}`}
      style={themeStyle}
    >
      <header className="bl-pub-stickercam__header">
        <h1 className="bl-pub-stickercam__title">{config.text.title}</h1>
        {config.text.subtitle && (
          <p className="bl-pub-stickercam__subtitle">{config.text.subtitle}</p>
        )}
      </header>

      <div className="bl-pub-stickercam__stage">
        <div className="bl-pub-stickercam__camera" aria-label="Camera viewfinder placeholder">
          <div className="bl-pub-stickercam__camera-fake">
            <span aria-hidden="true">📷</span>
            <span className="bl-pub-stickercam__camera-fake-text">Live camera goes here</span>
          </div>
          {bursts.map((b) => (
            <span
              key={b.id}
              className="bl-pub-stickercam__burst"
              style={{ left: `${b.x}%`, top: `${b.y}%` }}
              aria-hidden="true"
            >
              {b.emoji}
            </span>
          ))}
        </div>

        <ul className="bl-pub-stickercam__chips" aria-label="Class triggers">
          {classes.map((c) => {
            const cust = config.perClass[c.id];
            return (
              <li key={c.id} className="bl-pub-stickercam__chip">
                <span className="bl-pub-stickercam__chip-emoji" aria-hidden="true">
                  {cust?.emoji ?? '✨'}
                </span>
                <span className="bl-pub-stickercam__chip-name">{cust?.label ?? c.name}</span>
              </li>
            );
          })}
        </ul>
        {/* Optional widgets render in the order the kid chose. We
            iterate `resolvedOrder` and render each that is enabled,
            so reordering in the composer reorders the preview. */}
        {resolvedOrder.map((id) => {
          if (!config.widgets?.[id]) return null;
          if (id === 'bigBanner') {
            return demoTop ? (
              <BigBanner key={id} classId={demoTop} classes={classes} config={config} />
            ) : null;
          }
          if (id === 'confidenceBars') {
            return <PreviewConfidenceBars key={id} config={config} classes={classes} />;
          }
          if (id === 'scoreStreak') {
            return <ScoreStreak key={id} liveTop={demoTop} />;
          }
          if (id === 'historyStrip') {
            return <HistoryStrip key={id} liveTop={demoTop} classes={classes} config={config} />;
          }
          if (id === 'qrCode') {
            return <QrChip key={id} />;
          }
          if (id === 'shareLinks') {
            return <SharePreview key={id} />;
          }
          return null;
        })}
      </div>

      {config.branding.showFooter && (
        <footer className="bl-pub-stickercam__footer">
          Made on <strong>Bryta</strong>
        </footer>
      )}
    </div>
  );
}

/** Demo "top label" rotator — fires a fresh class id every ~2s when
 *  there's no liveLabelId. Drives the banner, score, and history
 *  strip in the preview pane. Production runtime supplies the real
 *  top class. */
function useDemoTopLabel(
  classes: ReadonlyArray<Pick<Class, 'id'>>,
  externalLive: string | null,
): string | null {
  const [top, setTop] = useState<string | null>(null);
  useEffect(() => {
    if (externalLive !== null && externalLive !== undefined) {
      setTop(externalLive);
      return;
    }
    if (classes.length === 0) return;
    const id = window.setInterval(() => {
      const idx = Math.floor(Math.random() * classes.length);
      setTop(classes[idx]?.id ?? null);
    }, 2000);
    return () => window.clearInterval(id);
  }, [classes, externalLive]);
  return top;
}

function BigBanner({
  classId,
  classes,
  config,
}: {
  classId: string;
  classes: ReadonlyArray<Pick<Class, 'id' | 'name'>>;
  config: PublishConfig;
}): JSX.Element {
  const cls = classes.find((c) => c.id === classId);
  const cust = config.perClass[classId];
  return (
    <div className="bl-pub-stickercam__banner" role="status">
      <span className="bl-pub-stickercam__banner-emoji" aria-hidden="true">
        {cust?.emoji ?? '✨'}
      </span>
      <span className="bl-pub-stickercam__banner-copy">
        Looks like <strong>{cust?.label ?? cls?.name ?? '...'}</strong>
      </span>
    </div>
  );
}

function ScoreStreak({ liveTop }: { liveTop: string | null }): JSX.Element {
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const lastRef = useRef<string | null>(null);
  useEffect(() => {
    if (!liveTop) return;
    if (liveTop !== lastRef.current) {
      // Score counts distinct class transitions — only a new class
      // pumps it. Streak resets and starts at 1 for the new class.
      setScore((s) => s + 1);
      setStreak(1);
    } else {
      // Same class as last hit — streak grows, score holds.
      setStreak((s) => s + 1);
    }
    lastRef.current = liveTop;
  }, [liveTop]);
  return (
    <div className="bl-pub-stickercam__score">
      <div>
        <p className="bl-pub-stickercam__score-num">{score}</p>
        <p className="bl-pub-stickercam__score-label">Score</p>
      </div>
      <div>
        <p className="bl-pub-stickercam__score-num">{streak}</p>
        <p className="bl-pub-stickercam__score-label">Streak</p>
      </div>
    </div>
  );
}

function HistoryStrip({
  liveTop,
  classes,
  config,
}: {
  liveTop: string | null;
  classes: ReadonlyArray<Pick<Class, 'id' | 'name'>>;
  config: PublishConfig;
}): JSX.Element {
  const [history, setHistory] = useState<string[]>([]);
  useEffect(() => {
    if (!liveTop) return;
    setHistory((h) => [liveTop, ...h].slice(0, 10));
  }, [liveTop]);
  return (
    <ul className="bl-pub-stickercam__history" aria-label="Recent predictions">
      {history.map((id, i) => {
        const cls = classes.find((c) => c.id === id);
        const cust = config.perClass[id];
        return (
          <li
            key={`${id}-${i}`}
            className="bl-pub-stickercam__history-chip"
            style={{ opacity: 1 - i * 0.1 }}
          >
            <span aria-hidden="true">{cust?.emoji ?? '✨'}</span>
            <span>{cust?.label ?? cls?.name ?? '?'}</span>
          </li>
        );
      })}
    </ul>
  );
}

/** Visual stand-in for the published page's share cluster. The
 *  buttons are intentionally inert in the composer — the kid is
 *  picking layout + colour, not actually copying their own preview
 *  URL. The published page replaces this with the wired version. */
function SharePreview(): JSX.Element {
  return (
    <div className="bl-pub-stickercam__share" aria-label="Share buttons (preview)">
      <p className="bl-pub-stickercam__share-eyebrow">Show off</p>
      <div className="bl-pub-stickercam__share-row">
        <span className="bl-pub-stickercam__share-btn">
          <span aria-hidden="true">🔗</span>
          <span>Copy link</span>
        </span>
        <span className="bl-pub-stickercam__share-btn">
          <span aria-hidden="true">📤</span>
          <span>Send</span>
        </span>
        <span className="bl-pub-stickercam__share-btn">
          <span aria-hidden="true">✉️</span>
          <span>Email</span>
        </span>
      </div>
    </div>
  );
}

function QrChip(): JSX.Element {
  return (
    <div className="bl-pub-stickercam__qr">
      <span className="bl-pub-stickercam__qr-thumb" aria-hidden="true">
        {/* Placeholder QR pattern — the real published page renders
            a live QR pointing at its own URL via the runtime. */}
        <span>▣</span>
      </span>
      <span className="bl-pub-stickercam__qr-copy">Scan with a phone to try it</span>
    </div>
  );
}

/** Confidence-bars widget for the live preview. Drives the bars from
 *  a slowly-rotating demo probability vector so the kid sees what
 *  the published page will look like — same shape and feel as the
 *  studio's testing predict panel. */
function PreviewConfidenceBars({
  config,
  classes,
}: {
  config: PublishConfig;
  classes: ReadonlyArray<Pick<Class, 'id' | 'name'>>;
}): JSX.Element {
  const probs = useDemoProbs(classes.length);
  const top = probs.indexOf(Math.max(...probs));
  const topClass = classes[top];
  const topCust = topClass ? config.perClass[topClass.id] : undefined;
  return (
    <section className="bl-pub-stickercam__bars" aria-label="Confidence bars">
      <p className="bl-pub-stickercam__bars-title">
        How sure am I?
        {topClass && (
          <span className="bl-pub-stickercam__bars-top">
            Looks like <strong>{topCust?.label ?? topClass.name}</strong>.
          </span>
        )}
      </p>
      <ul className="bl-pub-stickercam__bars-list">
        {classes.map((c, i) => {
          const cust = config.perClass[c.id];
          const pct = Math.round((probs[i] ?? 0) * 100);
          return (
            <li key={c.id} className="bl-pub-stickercam__bar">
              <span className="bl-pub-stickercam__bar-emoji" aria-hidden="true">
                {cust?.emoji ?? '✨'}
              </span>
              <span className="bl-pub-stickercam__bar-name">{cust?.label ?? c.name}</span>
              <span className="bl-pub-stickercam__bar-track">
                <span className="bl-pub-stickercam__bar-fill" style={{ width: `${pct}%` }} />
              </span>
              <span className="bl-pub-stickercam__bar-pct">{pct}%</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
